"use client";

import { useState, useRef, useLayoutEffect, useCallback, useId } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  width?: string;
}

export function Tooltip({ content, children, width = "w-56" }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; flipped: boolean } | null>(null);
  const id = useId();

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const tr = trigger.getBoundingClientRect();
    const pr = panel.getBoundingClientRect();
    const margin = 8;
    const gap = 8;

    let top = tr.top - pr.height - gap;
    let flipped = false;
    if (top < margin) {
      top = tr.bottom + gap;
      flipped = true;
    }

    let left = tr.left + tr.width / 2 - pr.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - pr.width - margin));

    setPos({ top, left, flipped });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  const arrowLeft = (() => {
    if (!pos || !triggerRef.current || !panelRef.current) return "50%";
    const tr = triggerRef.current.getBoundingClientRect();
    const triggerCenter = tr.left + tr.width / 2;
    const offset = triggerCenter - pos.left;
    return `${offset}px`;
  })();

  return (
    <span className="relative inline-flex items-baseline">
      <span
        ref={triggerRef}
        tabIndex={0}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>

      {open &&
        createPortal(
          <span
            ref={panelRef}
            id={id}
            role="tooltip"
            style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
            className={`fixed ${width} z-[9999] rounded-xl bg-neutral-900 text-white text-xs p-3 shadow-xl pointer-events-none`}
          >
            {content}
            <span
              className={`absolute ${pos?.flipped ? "bottom-full border-b-neutral-900 border-t-transparent" : "top-full border-t-neutral-900 border-b-transparent"} border-4 border-l-transparent border-r-transparent`}
              style={{ left: arrowLeft, transform: "translateX(-50%)" }}
              aria-hidden
            />
          </span>,
          document.body,
        )}
    </span>
  );
}
